/* HP launch welcome notice.
 *
 * Shows once per browsing session, on arrival, after the cookie consent banner
 * has been answered. Informational only: no tracking, no email capture, no
 * third-party script, no network request of any kind.
 *
 * NOT A MODAL (2026-09-16, popup-unlock)
 *   This used to be a centred modal: scrim, body.overflow = hidden, focus trap,
 *   aria-modal. A first-time visitor could not scroll the catalogue until they
 *   had dismissed it, and below-the-fold images never loaded until then. It is
 *   now a non-modal panel anchored to the bottom of the viewport:
 *     - no backdrop and no scroll lock: the page stays scrollable and clickable;
 *     - focus moves into the panel ONCE, so keyboard and screen-reader users
 *       know it is there, and is never trapped - Tab leaves it freely. If the
 *       visitor is already typing somewhere, focus is left where it is;
 *     - Escape closes it while focus is inside it; the close control and the
 *       footer button close it from anywhere;
 *     - any click on the page outside the panel dismisses it (light dismiss):
 *       using the site is the dismissal;
 *     - leaving the page with it still open counts as having seen it, so it
 *       does not follow the visitor from page to page. A modal could not be
 *       navigated past; a panel can, and a notice that re-appears on every
 *       page until it is clicked would be a new nuisance;
 *     - on dismissal, focus goes back to the element that had it, or to <main>
 *       on a fresh load where that element is <body> (whose focus() is a
 *       no-op) - never left on a control that is about to be hidden. If the
 *       visitor has already moved focus elsewhere, it is not touched.
 *   Same pattern as assets/hp-quote-ack.js. Do not add the lock back.
 *
 * Consent behaviour (EU law: the consent banner takes priority)
 *   - Never opens while Shopify's native privacy banner or preferences dialog
 *     is on screen, for as long as that takes.
 *   - Opens once the Customer Privacy API reports consent collected, or once a
 *     banner that was on screen has gone.
 *   - If no consent UI appears in the DOM at all within NO_BANNER_GRACE_MS,
 *     there is nothing to collide with, so it opens. Note that
 *     `shouldShowBanner()` is NOT treated as proof that a banner is coming: it
 *     was observed returning true on this storefront while no banner DOM was
 *     ever rendered and consent had already been given. The DOM is the signal.
 *   - If a banner turns up after the notice is already open, the notice stands
 *     itself down (without counting as dismissed) and returns afterwards.
 *   - Sits at z-index 1000000; the native banner uses 2000000-2000002, so the
 *     banner paints above the notice even in the moment between those checks.
 *
 * SELECTOR WARNING - read before editing CONSENT_SELECTOR.
 *   `shopify-pc__banner` is an element **id**, not a class. Shopify emits
 *   `<section id="shopify-pc__banner" class="shopify-pc__banner__dialog">`.
 *   An earlier version of this file queried `.shopify-pc__banner` as a class,
 *   which matched 0 nodes on every load, so the stand-down never ran and the
 *   notice opened on top of a live consent banner. Verified against Shopify's
 *   delivered storefront-banner.js, which declares:
 *       banner: DialogId  = "shopify-pc__banner"      (id)
 *               DialogClass = "shopify-pc__banner__dialog"
 *       prefs:  WrapperId = "shopify-pc__prefs"       (id)
 *               WrapperClass = "shopify-pc__prefs"    (class - this one exists)
 *   Both spellings are therefore matched below. If you change this list, prove
 *   it by calling `privacyBanner.showBanner()` on the live storefront and
 *   checking that the notice actually stands down.
 */
(function () {
  'use strict';

  // Read by assets/hp-quote-ack.js as well ("has the welcome notice been dealt
  // with this session?"). Keep the key and the value '1' if you change anything.
  var STORAGE_KEY = 'hp:welcome-popup:v1';
  var SHOW_DELAY_MS = 500; // let the page settle before opening
  var POLL_MS = 400;
  var NO_BANNER_GRACE_MS = 5000; // no consent UI in the DOM -> nothing to wait for
  var CLOSE_ANIM_MS = 280; // must match the CSS transition
  var HTML_OPEN_CLASS = 'hp-welcome-open'; // suppresses the chat launcher on phones, see the CSS

  // Shopify's native consent UI, matched by id AND by class. See the warning above.
  var CONSENT_SELECTOR = [
    '#shopify-pc__banner',
    '#shopify-pc__prefs',
    '#shopify-pc__prefs__overlay',
    '#shopify-pc__prefs__dialog',
    '.shopify-pc__banner',
    '.shopify-pc__banner__dialog',
    '.shopify-pc__banner__wrapper',
    '.shopify-pc__prefs',
    '.shopify-pc__prefs__dialog',
    '.shopify-pc__prefs__overlay'
  ].join(', ');

  var root = document.getElementById('hp-welcome');
  if (!root) return;

  var dialog = root.querySelector('.hp-welcome__dialog');
  if (!dialog) return;

  var scroller = dialog.querySelector('[data-hp-welcome-scroll]');

  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var isOpen = false;
  var lastFocused = null;
  var consentWatch = null;
  var scrollObserver = null;

  /* ------------------------------------------------------------------ *
   * Session memory                                                      *
   * ------------------------------------------------------------------ */

  // "Dealt with" = dismissed, or left open while the visitor moved on to
  // another page. Either way it does not come back this session.
  function doneThisSession() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false; // storage blocked (private mode): show it rather than fail
    }
  }

  function rememberDone() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      /* nothing we can do, and nothing that should break the page */
    }
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------ *
   * Cookie consent                                                      *
   * ------------------------------------------------------------------ */

  function privacyApi() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    return cp && typeof cp.shouldShowBanner === 'function' ? cp : null;
  }

  // true = consent answered, false = banner still owed, null = API unavailable
  function consentAnswered() {
    var cp = privacyApi();
    if (!cp) return null;
    try {
      return !cp.shouldShowBanner();
    } catch (e) {
      return null;
    }
  }

  // Is any of Shopify's native consent UI actually on screen right now?
  // Matched by id and by class, then filtered down to elements that are really
  // painted and really intersect the viewport - so a banner parked off-screen
  // or left in the DOM after dismissal can never hold the notice back forever.
  function consentUiVisible() {
    var nodes = document.querySelectorAll(CONSENT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('hidden')) continue;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      var r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var vw = window.innerWidth || document.documentElement.clientWidth;
      if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) continue;
      return true;
    }
    return false;
  }

  function waitForConsent(done) {
    if (designMode) {
      done();
      return;
    }

    // Nudge Shopify to load the consent API if it has not already.
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {});
      } catch (e) {
        /* optional convenience only */
      }
    }

    var startedAt = Date.now();
    var sawConsentUi = false;
    var timer = null;

    function finish() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
      document.removeEventListener('visitorConsentCollected', tick);
      done();
    }

    function tick() {
      // Rule 1, absolute: while any consent UI is on screen, it owns the
      // viewport. Never open, however long that takes.
      if (consentUiVisible()) {
        sawConsentUi = true;
        return;
      }

      // Rule 2: the API says consent has been collected.
      if (consentAnswered() === true) {
        finish();
        return;
      }

      // Rule 3: a banner was on screen and has now gone.
      if (sawConsentUi) {
        finish();
        return;
      }

      // Rule 4: no consent UI has appeared in the DOM at all within the grace
      // period, so there is nothing to collide with and nothing to wait for.
      //
      // This branch matters: `shouldShowBanner()` was observed returning true on
      // this storefront while the banner rendered no DOM at all and
      // `currentVisitorConsent()` already reported analytics/marketing/
      // preferences = "yes". Treating that flag as "a banner is coming" would
      // hold the notice back indefinitely. The DOM is the reliable signal; the
      // flag is not. If a banner does turn up later, the watcher started in
      // open() takes the notice back down again.
      if (Date.now() - startedAt > NO_BANNER_GRACE_MS) {
        finish();
      }
    }

    document.addEventListener('visitorConsentCollected', tick);
    timer = window.setInterval(tick, POLL_MS);
    tick();
  }

  /* ------------------------------------------------------------------ *
   * Scroll affordance                                                   *
   * ------------------------------------------------------------------ *
   * The dismiss button is pinned in its own footer and never scrolls, so a
   * visitor is never stranded. The middle section can still overflow, and on
   * a phone the overlay scrollbar fades out of sight, so it needs a cue of
   * its own: a fade and a chevron above the footer while there is more below,
   * and a hairline under the top bar once it has been scrolled.            */

  function updateScrollState() {
    if (!scroller) return;
    var maxScroll = scroller.scrollHeight - scroller.clientHeight;
    var hasMoreBelow = maxScroll > 2 && maxScroll - scroller.scrollTop > 2;

    if (hasMoreBelow) dialog.classList.add('hp-welcome--more');
    else dialog.classList.remove('hp-welcome--more');

    if (scroller.scrollTop > 2) dialog.classList.add('hp-welcome--scrolled');
    else dialog.classList.remove('hp-welcome--scrolled');
  }

  function startScrollWatch() {
    if (!scroller) return;
    scroller.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    // The panel is capped in dvh, so the mobile URL bar collapsing changes
    // how much fits; web fonts landing late changes how much there is.
    if (typeof window.ResizeObserver === 'function' && !scrollObserver) {
      scrollObserver = new window.ResizeObserver(updateScrollState);
      scrollObserver.observe(scroller);
      for (var i = 0; i < scroller.children.length; i++) scrollObserver.observe(scroller.children[i]);
    }
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(updateScrollState)['catch'](function () {});
    }

    updateScrollState();
    window.requestAnimationFrame(updateScrollState);
  }

  function stopScrollWatch() {
    if (!scroller) return;
    scroller.removeEventListener('scroll', updateScrollState);
    window.removeEventListener('resize', updateScrollState);
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }
  }

  /* ------------------------------------------------------------------ *
   * Focus. Moved in once on open, never trapped, released on close.    *
   * ------------------------------------------------------------------ */

  // The visitor is in the middle of typing something: do not pull focus away.
  function visitorIsTyping() {
    var el = document.activeElement;
    if (!el || el === document.body) return false;
    var tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
  }

  // Somewhere focus can honestly go back to: a real element, still in the
  // page, outside this panel, not disabled, and actually rendered.
  function canReceiveFocus(el) {
    if (!el || el === document.body || el === document.documentElement) return false;
    if (typeof el.focus !== 'function') return false;
    if (!document.contains(el) || root.contains(el)) return false;
    if (el.hasAttribute('disabled')) return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  }

  // Runs before the panel is hidden, so focus is never left on a hidden
  // control. Only acts if focus is still inside the panel: after a light
  // dismiss the visitor has already put focus somewhere else on purpose.
  function releaseFocus() {
    var active = document.activeElement;
    var target = canReceiveFocus(lastFocused) ? lastFocused : null;
    lastFocused = null;

    if (!active || !root.contains(active)) return;

    if (!target) {
      // Fresh page load: the previously focused element was <body>, whose
      // focus() is a no-op. Hand focus to the main landmark instead so a
      // keyboard or screen-reader user carries on from the page content.
      target = document.getElementById('MainContent') || document.querySelector('main');
      if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    }

    if (!target) {
      if (typeof active.blur === 'function') active.blur();
      return;
    }
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  }

  // Bound to the panel wrapper, not to document, so that Escape is never taken
  // away from the consent dialog or anything else outside this panel: it
  // closes the notice only while focus is inside it, which is how a non-modal
  // dialog behaves. There is deliberately no Tab handling - no trap.
  function onKeydown(event) {
    var key = event.key;
    if (key === 'Escape' || key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  }

  // Light dismiss: a click anywhere on the page outside the panel closes it.
  // Capture phase, so a page component that stops propagation cannot hide the
  // click from us; the click itself is left alone and still does its job.
  function onDocumentClick(event) {
    if (!isOpen) return;
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (root.contains(target)) return;
    if (target.closest(CONSENT_SELECTOR)) return; // the consent UI is never a dismissal
    close();
  }

  // Leaving the page with the notice still open counts as having seen it.
  function onPageHide() {
    if (isOpen && !designMode) rememberDone();
  }

  /* ------------------------------------------------------------------ *
   * Open / close. No scroll lock, no focus trap - this is not a modal.  *
   * ------------------------------------------------------------------ */

  function open() {
    if (isOpen) return;
    if (!designMode && consentUiVisible()) return; // last guard before painting
    if (doneThisSession()) return;

    isOpen = true;
    startConsentWatch();
    lastFocused = document.activeElement;

    root.hidden = false;

    // Marks the page while the notice is on screen. On phones the CSS uses it
    // to take Shopify Inbox's chat launcher out of hit testing, because it sits
    // at z-index 2147483000 - above this panel - and otherwise swallows taps
    // aimed at the dismiss button. Removed again in close().
    document.documentElement.classList.add(HTML_OPEN_CLASS);

    if (scroller) scroller.scrollTop = 0;

    void root.offsetWidth; // flush styles so the transition actually runs
    root.classList.add('is-open');

    root.addEventListener('keydown', onKeydown, true);
    if (!designMode) document.addEventListener('click', onDocumentClick, true);

    // One deliberate focus move, and no trap afterwards.
    if (!visitorIsTyping()) {
      try {
        dialog.focus({ preventScroll: true });
      } catch (e) {
        dialog.focus();
      }
    }

    startScrollWatch();
  }

  // If a consent banner or preferences dialog appears while the notice is open,
  // the banner takes priority: stand the notice down without counting it as
  // dismissed, and queue it to come back once consent has been answered.
  function startConsentWatch() {
    if (designMode || consentWatch) return;
    consentWatch = window.setInterval(function () {
      if (!isOpen) return;
      if (consentUiVisible()) {
        close(false);
        scheduleShow();
      }
    }, POLL_MS);
  }

  function stopConsentWatch() {
    if (consentWatch) {
      window.clearInterval(consentWatch);
      consentWatch = null;
    }
  }

  function close(remember) {
    if (!isOpen) return;
    isOpen = false;
    stopConsentWatch();
    stopScrollWatch();

    if (remember !== false) rememberDone();

    root.classList.remove('is-open');
    root.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('click', onDocumentClick, true);

    // Give the chat launcher straight back - it is a real support channel and
    // is only ever suppressed for as long as this notice is on screen.
    document.documentElement.classList.remove(HTML_OPEN_CLASS);

    dialog.classList.remove('hp-welcome--more');
    dialog.classList.remove('hp-welcome--scrolled');

    // Before hiding, never after: focus must not be left on a hidden control.
    releaseFocus();

    if (prefersReducedMotion()) {
      root.hidden = true;
    } else {
      window.setTimeout(function () {
        if (!isOpen) root.hidden = true;
      }, CLOSE_ANIM_MS);
    }
  }

  // The close button and the "Continue" button both carry the attribute.
  root.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('[data-hp-welcome-close]')) {
      event.preventDefault();
      close();
    }
  });

  /* ------------------------------------------------------------------ *
   * Start                                                               *
   * ------------------------------------------------------------------ */

  function scheduleShow() {
    waitForConsent(function () {
      if (doneThisSession()) return;
      window.setTimeout(function () {
        if (!doneThisSession()) open();
      }, SHOW_DELAY_MS);
    });
  }

  if (doneThisSession()) return;
  window.addEventListener('pagehide', onPageHide);
  scheduleShow();
})();
